const SCROLL_TOP = { top: 0, left: 0, behavior: "auto" as ScrollBehavior };
const INNER_SCROLL_SELECTOR =
  "#root, .player-layout, .custom-container, .player-main-surface, .color-live-container";
const SCROLL_LOCK_CLASS = "bet-modal-scroll-lock";
const MOBILE_LOCK_MQ = "(max-width: 960px)";
let pageScrollLocked = false;
let sheetTouchStartY = 0;

function isMobileSheetLock() {
  return window.matchMedia(MOBILE_LOCK_MQ).matches;
}

function canOverflowScroll(node: HTMLElement) {
  const overflowY = window.getComputedStyle(node).overflowY;
  return (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay")
    && node.scrollHeight > node.clientHeight + 1;
}

function findSheetScrollParent(target: EventTarget | null): HTMLElement | null {
  let node = target as HTMLElement | null;
  while (node && node !== document.body && node.id !== "jocksportsModal") {
    if (canOverflowScroll(node)) return node;
    node = node.parentElement;
  }
  return null;
}

export function rememberSheetTouchStart(event: TouchEvent) {
  sheetTouchStartY = event.touches[0]?.clientY ?? 0;
}

/** Block page rubber-banding; only the slip list may pan, and not past its edges. */
export function preventSheetBackgroundScroll(event: Event) {
  if (!isMobileSheetLock()) return;
  const touchEvent = event as TouchEvent;
  const scrollParent = findSheetScrollParent(event.target);
  if (!scrollParent) {
    event.preventDefault();
    return;
  }
  if (!touchEvent.touches?.length) return;
  const delta = touchEvent.touches[0].clientY - sheetTouchStartY;
  const atTop = scrollParent.scrollTop <= 0;
  const atBottom = scrollParent.scrollTop + scrollParent.clientHeight >= scrollParent.scrollHeight - 1;
  if ((atTop && delta > 0) || (atBottom && delta < 0)) {
    event.preventDefault();
  }
}

function collectScrollNodes(): HTMLElement[] {
  const nodes: HTMLElement[] = [document.documentElement, document.body];
  document.querySelectorAll<HTMLElement>(INNER_SCROLL_SELECTOR).forEach((el) => {
    if (!nodes.includes(el)) nodes.push(el);
  });
  return nodes;
}

function clearLeftoverFreezeStyles() {
  document.documentElement.classList.remove(SCROLL_LOCK_CLASS);
  document.body.classList.remove(
    SCROLL_LOCK_CLASS,
    "bet-modal-body-fixed",
    "bet-modal-nav-compensate",
  );
  document.body.style.top = "";
  document.body.style.left = "";
  document.body.style.right = "";
  document.body.style.width = "";
  document.body.style.removeProperty("--bet-lock-y");
  document.documentElement.style.scrollBehavior = "";
  document.querySelectorAll<HTMLElement>(INNER_SCROLL_SELECTOR).forEach((el) => {
    el.style.removeProperty("overflow");
  });
  document.querySelectorAll<HTMLElement>(".player-layout").forEach((el) => {
    if (el.style.marginTop.startsWith("-")) el.style.removeProperty("margin-top");
  });
}

export function isPageScrollLocked() {
  return pageScrollLocked;
}

export function resetInnerScrollLocks() {
  unlockPageScroll();
  clearLeftoverFreezeStyles();
}

/** Do not move scroll. Only block gestures behind the mobile sheet. */
export function lockPageScroll() {
  if (pageScrollLocked || !isMobileSheetLock()) return;
  pageScrollLocked = true;
  document.documentElement.classList.add(SCROLL_LOCK_CLASS);
  document.body.classList.add(SCROLL_LOCK_CLASS);
  window.addEventListener("wheel", preventSheetBackgroundScroll, { passive: false });
  window.addEventListener("touchstart", rememberSheetTouchStart, { passive: true });
  window.addEventListener("touchmove", preventSheetBackgroundScroll, { passive: false });
}

export function unlockPageScroll(_restore = true) {
  window.removeEventListener("wheel", preventSheetBackgroundScroll);
  window.removeEventListener("touchstart", rememberSheetTouchStart);
  window.removeEventListener("touchmove", preventSheetBackgroundScroll);
  pageScrollLocked = false;
  clearLeftoverFreezeStyles();
}

if (typeof window !== "undefined" && window.matchMedia) {
  const mobileLockMq = window.matchMedia(MOBILE_LOCK_MQ);
  const onLockMqChange = (event: MediaQueryListEvent) => {
    if (!event.matches) unlockPageScroll();
  };
  if (typeof mobileLockMq.addEventListener === "function") {
    mobileLockMq.addEventListener("change", onLockMqChange);
  } else if (typeof mobileLockMq.addListener === "function") {
    mobileLockMq.addListener(onLockMqChange);
  }
  if (!mobileLockMq.matches) unlockPageScroll();
}

function disableBrowserScrollRestoration() {
  if ("scrollRestoration" in window.history) {
    window.history.scrollRestoration = "manual";
  }
}

function scrollNodeToTop(node: HTMLElement | null | undefined) {
  if (!node) return;
  node.scrollTop = 0;
  node.scrollLeft = 0;
  node.scrollTo?.(SCROLL_TOP);
}

/** Reset every container that can hold scroll in this SPA (window, html, body, #root, layouts). */
export function scrollPageToTop() {
  if (pageScrollLocked) return;
  disableBrowserScrollRestoration();
  window.scrollTo(SCROLL_TOP);
  scrollNodeToTop(document.documentElement);
  scrollNodeToTop(document.body);
  scrollNodeToTop(document.getElementById("root"));
  document.querySelectorAll(INNER_SCROLL_SELECTOR).forEach((node) => {
    scrollNodeToTop(node as HTMLElement);
  });
}

/** Keep pinning to top until layout settles. Stop if the user starts scrolling. */
export function scheduleScrollPageToTop(delaysMs: number[] = [50]) {
  let cancelled = false;
  const run = () => {
    if (cancelled) return;
    scrollPageToTop();
  };
  run();
  let innerFrame = 0;
  const frame = requestAnimationFrame(() => {
    run();
    innerFrame = requestAnimationFrame(run);
  });
  const timers = delaysMs.map((ms) => window.setTimeout(run, ms));

  const stop = () => {
    if (cancelled) return;
    cancelled = true;
    cancelAnimationFrame(frame);
    cancelAnimationFrame(innerFrame);
    timers.forEach((id) => window.clearTimeout(id));
    window.removeEventListener("wheel", stop);
    window.removeEventListener("touchmove", stop);
  };

  window.addEventListener("wheel", stop, { passive: true });
  window.addEventListener("touchmove", stop, { passive: true });
  return stop;
}

export function getPageScrollTop() {
  const nodes = collectScrollNodes();
  let innerTop = 0;
  nodes.forEach((node) => {
    innerTop = Math.max(innerTop, node.scrollTop ?? 0);
  });
  return Math.max(window.scrollY ?? 0, innerTop);
}

export function getDistanceFromPageBottom() {
  const nodes = collectScrollNodes();
  const scrollTop = getPageScrollTop();
  let scrollHeight = Math.max(
    document.documentElement.scrollHeight,
    document.body.scrollHeight,
  );
  nodes.forEach((node) => {
    scrollHeight = Math.max(scrollHeight, node.scrollHeight ?? 0);
  });
  const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
  return scrollHeight - scrollTop - viewportHeight;
}
